import { createContext, useContext, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";

type Atom = { read: () => unknown; restore: (value: unknown) => void };
export const RecoveryContext = createContext<{ register: (key: string, atom: Atom) => () => void; changed: (event?: Event) => void } | null>(null);

function encode(value: unknown): unknown {
  if (value instanceof Set) return { __celiaType: "set", values: [...value].map(encode) };
  if (value instanceof Map) return { __celiaType: "map", values: [...value].map(([k, v]) => [encode(k), encode(v)]) };
  if (value instanceof File || value instanceof Blob || typeof value === "function") throw new Error("Reselect local files to restore this input");
  if (Array.isArray(value)) return value.map(encode);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, encode(v)]));
  return value;
}
function decode(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(decode);
  if (value && typeof value === "object") {
    const x = value as Record<string, unknown>;
    if (x.__celiaType === "set" && Array.isArray(x.values)) return new Set(x.values.map(decode));
    if (x.__celiaType === "map" && Array.isArray(x.values)) return new Map(x.values.map(v => {
      const pair = v as [unknown, unknown]; return [decode(pair[0]), decode(pair[1])];
    }));
    return Object.fromEntries(Object.entries(x).filter(([k]) => !["__proto__", "constructor", "prototype"].includes(k)).map(([k, v]) => [k, decode(v)]));
  }
  return value;
}

/** Restore changes explicitly through the same setters used by the owner UI. */
export function useRecoverableState<S>(key: string, initial: S | (() => S)): [S, Dispatch<SetStateAction<S>>] {
  const [value, setValue] = useState(initial);
  const latest = useRef(value), lastReported = useRef(value), first = useRef(true);
  latest.current = value;
  const recovery = useContext(RecoveryContext);
  useEffect(() => recovery?.register(key, {
    read: () => encode(latest.current),
    restore: (saved) => setValue(decode(saved) as S),
  }), [key, recovery]);
  useEffect(() => {
    if (first.current) { first.current = false; lastReported.current = value; return; }
    // React StrictMode reconnects passive effects on mount. Do not save an unchanged
    // default merely because the recovery provider/effect was reconnected.
    if (Object.is(lastReported.current, value)) return;
    lastReported.current = value;
    recovery?.changed();
  }, [value, recovery]);
  return [value, setValue];
}
